import React, { useEffect, useState, useCallback, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { style } from "../../Styles";
import CustomTableComponents from "../CommonComponents/CustomTableComponents";
import { Card, message, Modal } from "antd";
import { MinusOutlined, ExclamationCircleFilled } from "@ant-design/icons";
import { getTicketsBetweenDate, cancelTickets } from "../../redux/ticketSlice";
import { getAllKiosks } from "../../redux/kiosksSlice";
import { formatDateAndTime } from "../../Utils/Imports/date-utils";
import Header from "../../global/Header/Header";
import dayjs from "dayjs";
import isBetween from "dayjs/plugin/isBetween";
import FilteringDrawer from "../Logs/Filter/FilteringDrawer";
import { useTranslation } from "react-i18next";

const today = dayjs().startOf("day");

const Ticket = () => {
  const { t } = useTranslation();
  dayjs.extend(isBetween);  // Add selected tickets state
  const [selectedTickets, setSelectedTickets] = useState([]);
  const [isCancellingTickets, setIsCancellingTickets] = useState(false);
  const preventNextSelection = useRef(false);

  const columns = [
    {
      title: t("ticketScreen.table.ticketNumber"),
      key: "id",
      render: (record) => {
        return (
          <span
            style={{
              color: record.cancelled ? "#ff4d4f" : "inherit",
            }}
          >
            {record?.id}
          </span>
        );
      },
      sorter: (a, b) => a.id - b.id,
      show: true,
    },
    {
      title: t("ticketScreen.table.referenceNumber"),
      key: "ticketReferenceNumber",
      render: (record) => {
        return (
          <span
            style={{
              color: record.cancelled ? "#ff4d4f" : "inherit",
            }}
          >
            {record.ticketReferenceNumber}
          </span>
        );
      },
      sorter: (a, b) => a.ticketReferenceNumber - b.ticketReferenceNumber,
      show: true,
    },
    {
      title: t("ticketScreen.table.printedDate"),
      key: "printedDate",
      render: (record) => {
        return record?.created ? (
          <span
            style={{
              color: record.cancelled ? "#ff4d4f" : "inherit",
            }}
          >
            {formatDateAndTime(record?.created)}
          </span>
        ) : (
          <span
            style={{
              color: record.cancelled ? "#ff4d4f" : "inherit",
            }}
          >
            <MinusOutlined />
          </span>
        );
      },
      sorter: (a, b) => {
        const dateA = new Date(a.created);
        const dateB = new Date(b.created);
        return dateA.getTime() - dateB.getTime();
      },
      show: true,
    },
    {
      title: t("ticketScreen.table.bundle"),
      key: "bundle",
      render: (record) => {
        return (
          <span
            style={{
              color: record.cancelled ? "#ff4d4f" : "inherit",
            }}
          >
            {record.bundle?.name}
          </span>
        );
      },
      sorter: (a, b) => a.bundle?.name.length - b.bundle?.name.length,
      show: true,
    },
    {
      title: t("ticketScreen.table.numberOfScans"),
      key: "numberOfScan",
      render: (record) => {
        return (
          <span
            style={{
              ...style.dataContainer,
              color: record.cancelled ? "#ff4d4f" : "inherit",
            }}
          >
            {record.numberOfScan}/{record?.bundle?.numberOfTrips}{" "}
          </span>
        );
      },
      sorter: (a, b) => a.numberOfScan - b.numberOfScan,
      show: true,
    },
    {
      title: t("ticketScreen.table.driver"),
      key: "driver",
      render: (record) => {
        return record?.driver ? (
          <span
            style={{
              ...style.dataContainer,
              color: record.cancelled ? "#ff4d4f" : "inherit",
            }}
          >
            {record.driver.name} {record.driver.surname}
          </span>
        ) : (
          <span
            style={{
              color: record.cancelled ? "#ff4d4f" : "inherit",
            }}
          >
            <MinusOutlined />
          </span>
        );
      },
      sorter: (a, b) => a.name - b.name,
      show: true,
    },
    {
      title: t("ticketScreen.table.status"),
      key: "status",
      render: (record) => {
        return (
          <span
            style={{
              ...style.dataContainer,
              color: record.cancelled ? "#ff4d4f" : "inherit",
            }}
          >
            <div
              style={record.cancelled ? style.statusInactif : style.statusActif}
            >
              {record.cancelled
                ? t("ticketScreen.table.cancelled")
                : t("ticketScreen.table.active")}
            </div>
          </span>
        );
      },
      sorter: (a, b) => (a.cancelled ? 1 : 0) - (b.cancelled ? 1 : 0),
      show: true,
    },
    {
      title: t("ticketScreen.table.statusTimestamp"),
      key: "statusTimestamp",
      render: (record) => {
        return record.cancelledStatusUpdatedAt ? (
          <span
            style={{
              fontSize: "12px",
              color: record.cancelled ? "#ff4d4f" : "#666",
              width: "100px",
              maxWidth: "100px",
              display: "inline-block",
              textAlign: "center",
            }}
          >
            {formatDateAndTime(record.cancelledStatusUpdatedAt)}
          </span>
        ) : (
          <span
            style={{
              width: "100px",
              maxWidth: "100px",
              display: "inline-block",
              textAlign: "center",
              color: record.cancelled ? "#ff4d4f" : "inherit",
            }}
          >
            <MinusOutlined />
          </span>
        );
      },
      sorter: (a, b) => {
        const dateA = new Date(a.cancelledStatusUpdatedAt || 0);
        const dateB = new Date(b.cancelledStatusUpdatedAt || 0);
        return dateA.getTime() - dateB.getTime();
      },
      show: true,
    },
  ];

  const [filter, setFilter] = useState("");
  const [dateFilter, setDateFilter] = useState([today, today]);
  const [isMakingApiCall, setMakingApiCall] = useState(true);
  const [csvHeaders, setCsvHeaders] = useState([
    {
      label: t("ticketScreen.table.ticketNumber"),
      key: "id",
    },
    {
      label: t("ticketScreen.table.referenceNumber"),
      key: "ticketReferenceNumber",
    },
    {
      label: t("ticketScreen.table.status"),
      key: "status",
    },
    {
      label: t("ticketScreen.table.statusTimestamp"),
      key: "statusTimestamp",
    },
    {
      label: t("ticketScreen.table.printedDate"),
      key: "created",
    },
    {
      label: t("ticketScreen.table.bundle"),
      key: "bundleName",
    },
    {
      label: t("ticketScreen.table.numberOfScans"),
      key: "numberOfScan",
    },
    {
      label: t("ticketScreen.table.driver"),
      key: "driver",
    },
  ]);
  const [isFilerDrawerOpen, setisFilerDrawerOpen] = useState(false);
  const [selectedOption, setSelectedOption] = useState(null);
  // Add state for status filter dropdown
  const [selectedOptionOfDropdownFields, setSelectedOptionOfDropdownFields] =
    useState({
      status: null,
    });

  const dispatch = useDispatch();
  const { tickets } = useSelector((state) => state.tickets);
  const { kiosks } = useSelector((state) => state.kiosks);
  // Ensure tickets is always an array
  const ticketsArray = tickets || [];
  const kiosksList = kiosks?.kiosksList || [];
  // Status filter dropdown configuration
  const statusFilterOptions = [
    { id: "active", value: false, name: t("ticketScreen.table.active") },
    { id: "cancelled", value: true, name: t("ticketScreen.table.cancelled") },
  ];

  const fieldsOfDropdowns = [
    {
      label: t("ticketScreen.drawer.ticketStatus"),
      key: "status",
      id: "status",
      options: statusFilterOptions,
      optionLabel: "name",
      optionValue: "value",
    },
  ];

  // Helper functions for selection logic
  const getSelectionMode = () => {
    if (selectedTickets.length === 0) return null;
    return selectedTickets[0].cancelled ? "cancelled" : "active";
  };

  const canSelectTicket = (ticket) => {
    const selectionMode = getSelectionMode();
    if (!selectionMode) return true; // No selection yet, can select anything
    return selectionMode === "cancelled" ? ticket.cancelled : !ticket.cancelled;
  };

  const getButtonText = () => {
    const selectionMode = getSelectionMode();
    if (selectedTickets.length === 0) {
      return t("ticketScreen.selectTicketsToAction");
    }

    if (selectionMode === "cancelled") {
      return selectedTickets.length > 1
        ? t("ticketScreen.uncancelTickets", { count: selectedTickets.length })
        : t("ticketScreen.uncancelTicket");
    } else {
      return selectedTickets.length > 1
        ? t("ticketScreen.cancelTickets", { count: selectedTickets.length })
        : t("ticketScreen.cancelTicket");
    }
  };
    // Cancel tickets handler with confirmation modal
  const showConfirmationModal = () => {
    if (selectedTickets.length === 0) {
      message.warning(t("ticketScreen.messages.selectTickets"));
      return;
    }

    const cancelledCount = selectedTickets.filter(ticket => !ticket.cancelled).length;
    const uncancelledCount = selectedTickets.filter(ticket => ticket.cancelled).length;
    
    let title, content;
    
    if (cancelledCount > 0 && uncancelledCount > 0) {
      title = t("ticketScreen.confirmModal.cancelTitle");
      content = t("ticketScreen.confirmModal.mixedMessage", { count: selectedTickets.length });
    } else if (cancelledCount > 0) {
      title = t("ticketScreen.confirmModal.cancelTitle");
      content = t("ticketScreen.confirmModal.cancelMessage", { count: cancelledCount });
    } else {
      title = t("ticketScreen.confirmModal.uncancelTitle");
      content = t("ticketScreen.confirmModal.uncancelMessage", { count: uncancelledCount });
    }

    Modal.confirm({
      title,
      content,
      icon: <ExclamationCircleFilled style={{ color: "#4d609b" }} />,
      okText: t("ticketScreen.confirmModal.confirm"),
      cancelText: t("ticketScreen.confirmModal.cancel"),
      cancelButtonProps: {
        style: {
          color: "#4d609b",
          backgroundColor: "white",
          borderColor: "#4d609b",
        },
      },
      okButtonProps: {
        style: {
          color: "white",
          backgroundColor: "#4d609b",
        },
      },
      onOk: handleCancelTickets,
      onCancel: () => {},
    });
  };

  const handleCancelTickets = async () => {
    setIsCancellingTickets(true);
    try {
      await dispatch(cancelTickets(selectedTickets)).unwrap();

      const cancelledCount = selectedTickets.filter(
        (ticket) => !ticket.cancelled
      ).length;
      const uncancelledCount = selectedTickets.filter(
        (ticket) => ticket.cancelled
      ).length;

      if (cancelledCount > 0 && uncancelledCount > 0) {
        message.success(t("ticketScreen.messages.mixedCancelSuccess"));
      } else if (cancelledCount > 0) {
        message.success(
          t("ticketScreen.messages.cancelSuccess", { count: cancelledCount })
        );
      } else {
        message.success(
          t("ticketScreen.messages.uncancelSuccess", {
            count: uncancelledCount,
          })
        );
      }

      // Clear selection and refresh tickets
      setSelectedTickets([]);
      fetchTickets();
    } catch (error) {
      message.error(t("ticketScreen.messages.cancelError"));
      console.error("Cancel ticket error:", error);
    } finally {
      setIsCancellingTickets(false);
    }
  };
  const fetchTickets = useCallback(() => {
    setMakingApiCall(true);
    // Set start to 00:00
    const start = dateFilter[0]
      .startOf("day")
      .format("YYYY-MM-DDTHH:mm:ss.SSS[Z]");

    // Set end to 23:59
    const end = dateFilter[1].endOf("day").format("YYYY-MM-DDTHH:mm:ss.SSS[Z]");
    dispatch(
      getTicketsBetweenDate({
        start: start !== undefined ? start : null,
        end: end !== undefined ? end : null,
        kioskId: selectedOption,
      })
    )
      .unwrap()
      .then((data) => {
        setMakingApiCall(false);
      })
      .catch(() => setMakingApiCall(false));
  }, [dispatch, dateFilter, selectedOption]);

  useEffect(() => {
    fetchTickets();
  }, [fetchTickets]);

  const fetchKiosks = useCallback(async () => {
    setMakingApiCall(true);
    dispatch(getAllKiosks())
      .unwrap()
      .then(() => {})
      .catch((err) => {
        message.error("Error while fetching kiosks");
      })
      .finally(() => setMakingApiCall(false));
  }, [dispatch]);

  useEffect(() => {
    fetchKiosks();
  }, [fetchKiosks]);

  const isDateWithinRange = (itemDate, dateFilter) => {
    if (dateFilter.length === 2 && dateFilter[0] && dateFilter[1]) {
      const startDate = dayjs(dateFilter[0]);
      const endDate = dayjs(dateFilter[1]);
      return (
        itemDate.isSame(startDate) ||
        itemDate.isSame(endDate) ||
        itemDate.isBetween(startDate, endDate, null, "[)")
      );
    }
    return true;
  };

  const matchesFilter = (item, filter) => {
    const filterLower = filter.trim().toLowerCase();
    return (
      item?.ticketReferenceNumber
        ?.toString()
        .toLowerCase()
        .includes(filterLower) ||
      item?.created?.toString().toLowerCase().includes(filterLower) ||
      item?.bundle?.name?.toString().toLowerCase().includes(filterLower)
    );
  };
  const filterTickets = (tickets, filter, dateFilter, statusFilter) => {
    return (
      tickets?.filter((item) => {
        const itemDate = dayjs(item?.created).format("YYYY-MM-DD");
        const isWithinRange = isDateWithinRange(dayjs(itemDate), dateFilter);
        const matchesTextFilter = matchesFilter(item, filter);

        // Apply status filter
        let matchesStatusFilter = true;
        if (statusFilter !== null && statusFilter !== undefined) {
          // statusFilter is now a boolean: true for cancelled, false for active
          matchesStatusFilter = item.cancelled === statusFilter;
        }

        return matchesTextFilter && isWithinRange && matchesStatusFilter;
      }) || []
    );
  };

  const filteredData = filterTickets(
    ticketsArray,
    filter,
    dateFilter,
    selectedOptionOfDropdownFields.status
  );

  const onFilterByDate = (date, dateString) => {
    const startDate = dayjs(date[0]);
    const endDate = dayjs(date[1]);
    setDateFilter([startDate, endDate]);
  };

  const openFilterDrawer = () => {
    setisFilerDrawerOpen(true);
  };
  const closeFilterDrawer = () => {
    setisFilerDrawerOpen(false);
  };

  const onSaveFilter = () => {
    fetchTickets();
    closeFilterDrawer();
  };

  // Reset filter function to clear all filters including status
  const resetFilter = () => {
    setSelectedOption(null);
    setSelectedOptionOfDropdownFields({ status: null });
    fetchTickets();
  };

  const createdCsvData = (filteredData || []).map((item) => {
    return {
      ...item,
      created: formatDateAndTime(item.created),
      bundleName: item?.bundle?.name,
      driver: item?.driver
        ? item?.driver.name + " " + item?.driver.surname
        : "-",
      status: item.cancelled
        ? t("ticketScreen.table.cancelled")
        : t("ticketScreen.table.active"),
      statusTimestamp: item.cancelledStatusUpdatedAt
        ? formatDateAndTime(item.cancelledStatusUpdatedAt)
        : "-",
    };
  });
  const handleGenerateCsv = (event, done) => {
    const promise = async () => {
      setCsvHeaders([
        {
          label: t("ticketScreen.table.ticketNumber"),
          key: "id",
        },
        {
          label: t("ticketScreen.table.referenceNumber"),
          key: "ticketReferenceNumber",
        },
        {
          label: t("ticketScreen.table.status"),
          key: "status",
        },
        {
          label: t("ticketScreen.table.statusTimestamp"),
          key: "statusTimestamp",
        },
        {
          label: t("ticketScreen.table.printedDate"),
          key: "created",
        },
        {
          label: t("ticketScreen.table.bundle"),
          key: "bundleName",
        },
        {
          label: t("ticketScreen.table.numberOfScans"),
          key: "numberOfScan",
        },
        {
          label: t("ticketScreen.table.driver"),
          key: "driver",
        },
      ]);
    };

    promise().then(done());
  };// Handle single row click (for compatibility with CustomTableComponents)  // Enhanced select all functionality
  const handleSelectAll = (selected, selectedRows, changeRows) => {
    if (selected) {
      // User clicked "select all" - show modal to choose which type to select
      preventNextSelection.current = true;
      showSelectAllModal();
    } else {
      // User clicked to deselect all
      setSelectedTickets([]);
    }
  };
  const showSelectAllModal = () => {
    const activeTickets = filteredData.filter(ticket => !ticket.cancelled);
    const cancelledTickets = filteredData.filter(ticket => ticket.cancelled);
    
    const modal = Modal.confirm({
      title: t("ticketScreen.selectAll.title"),
      content: (
        <div>
          <p>{t("ticketScreen.selectAll.message")}</p>
          <div style={{ marginTop: 16, display: 'flex', gap: 8, flexDirection: 'column' }}>
            <button 
              style={{
                padding: '8px 16px',
                border: '1px solid #4d609b',
                backgroundColor: '#4d609b',
                color: 'white',
                borderRadius: '4px',
                cursor: 'pointer'
              }}              onClick={() => {
                setSelectedTickets(activeTickets);
                preventNextSelection.current = false; // Reset the flag
                modal.destroy();
              }}
            >
              {t("ticketScreen.selectAll.selectAllActive")} ({activeTickets.length})
            </button>
            <button 
              style={{
                padding: '8px 16px',
                border: '1px solid #4d609b',
                backgroundColor: 'white',
                color: '#4d609b',
                borderRadius: '4px',
                cursor: 'pointer'
              }}              onClick={() => {
                setSelectedTickets(cancelledTickets);
                preventNextSelection.current = false; // Reset the flag
                modal.destroy();
              }}
            >
              {t("ticketScreen.selectAll.selectAllCancelled")} ({cancelledTickets.length})
            </button>
          </div>
        </div>
      ),
      icon: null,
      okText: null,
      cancelText: t("ticketScreen.selectAll.cancel"),
      cancelButtonProps: {
        style: {
          color: "#4d609b",
          backgroundColor: "white",
          borderColor: "#4d609b",
        },
      },      onOk: () => false, // Prevent default OK behavior
      onCancel: () => {
        // Reset the flag when user cancels
        preventNextSelection.current = false;
      },
    });
  };

  // Handle single row click (for compatibility with CustomTableComponents)
  const handleRowClick = (record) => {
    // Check if this ticket can be selected based on current selection mode
    if (!canSelectTicket(record)) {
      const selectionMode = getSelectionMode();
      const modeText =
        selectionMode === "cancelled"
          ? t("ticketScreen.table.cancelled")
          : t("ticketScreen.table.active");
      message.warning(
        t("ticketScreen.messages.canOnlySelect", { mode: modeText })
      );
      return;
    }

    // Toggle selection of the clicked row
    const isSelected = selectedTickets.some(
      (ticket) => ticket?.id === record?.id
    );
    if (isSelected) {
      // Remove from selection
      const newSelection = selectedTickets.filter(
        (ticket) => ticket?.id !== record?.id
      );
      setSelectedTickets(newSelection);
    } else {
      // Add to selection
      setSelectedTickets([...selectedTickets, record]);
    }
  };

  // Handle dropdown field changes
  const handleSelectDropdownsChange = (key, value) => {
    setSelectedOptionOfDropdownFields((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  return (
    <>
      <Header
        onAddButtonClick={() => {}}
        onSearch={(e) => {
          setFilter(e.target.value);
        }}
        searchPlaceholder={t("ticketScreen.header.search")}
        addButtonTextColor={"#ffffff"}
        onReload={fetchTickets}
        showAddButton={false}
        BreadcrumbItems={[
          {
            title: t("ticketScreen.header.title"),
          },
        ]}
        showBreadcrumb={true}
        showExportCSV={true}
        csvHeaders={csvHeaders}
        csvData={createdCsvData}
        generateCSVfn={handleGenerateCsv}
        csvDataMiddleFileName={"Ticket"}
        showFilterByDate={true}
        onFilterByDate={(date, dateString) => onFilterByDate(date, dateString)}
        showFilterButton={true}
        onClickFilterButton={() => {
          openFilterDrawer();
        }}        dateRange={dateFilter}        showChangeStatusButton={true}
        ChangeStatusButtonText={getButtonText()}
        disableChangeStatusButton={selectedTickets.length === 0 || isCancellingTickets}
        onClickChangeStatusButton={showConfirmationModal}
      />
      <Card style={{ margin: 16, height: "85vh" }}>
        {" "}
        <CustomTableComponents
          isMakingApiCall={isMakingApiCall}
          columns={columns.filter((column) => column.show)}
          dataArray={filteredData}
          handleDoubleClickRow={() => {}}
          handleSelectedRow={handleRowClick}
          onRow={(record) => ({
            style: {
              color: record.cancelled ? "#ff4d4f" : "inherit",
            },
          })}
          rowSelection={{
            selectedRowKeys: selectedTickets
              .map((ticket) => ticket?.id)
              .filter((id) => id !== undefined),
            onChange: (selectedRowKeys, selectedRows) => {
              // Check if we should prevent this selection change
              if (preventNextSelection.current) {
                preventNextSelection.current = false;
                return; // Don't update selection
              }
              setSelectedTickets(selectedRows || []);
            },
            onSelectAll: handleSelectAll,
            getCheckboxProps: (record) => ({
              disabled: !canSelectTicket(record),
            }),
            hideSelectAll: false, // Keep the select all checkbox visible
          }}
        />
        {isFilerDrawerOpen && (
          <FilteringDrawer
            closeFilteringDrawer={closeFilterDrawer}
            isFilteringDrawerOpened={isFilerDrawerOpen}
            resetFilter={resetFilter}
            loading={false}
            formName={"Tickets filter"}
            title={t("ticketScreen.drawer.title")}
            onSave={() => onSaveFilter()}
            displayDateRange={false}
            selectLabel={t("ticketScreen.drawer.kiosks")}
            selectValue={"name"}
            selectKey={"name"}
            data={kiosksList || []}
            optionValue={"name"}
            selectedOption={selectedOption}
            setSelectedOption={setSelectedOption}
            fieldsOfDropdowns={fieldsOfDropdowns}
            selectedOptionOfDropdownFields={selectedOptionOfDropdownFields}
            setSelectedOptionOfDropdownFields={handleSelectDropdownsChange}
          />
        )}
      </Card>
    </>
  );
};

export default Ticket;
